/*
 * The standard emoji as Beacon offers them: Unicode's, each by its Emojibase name, and by the
 * shortcodes Slack gives it (iamcal's, so `:tada:` and `:+1:` mean in Beacon what they mean there).
 * An emoji with no shortcode is offered by the picker alone.
 */

/**
 * One emoji as the data describes it: the emoji itself, its name, words that find it, and the emoji
 * it is in each skin tone, for the hands and people that have them, by tone (the picker's reading
 * of Emojibase). Emojibase's own file lists its tones as entries instead, which the catalog leaves.
 */
export type EmojiEntry = {
  emoji: string
  label: string
  tags?: readonly string[]
  skins?: Readonly<Record<string, string>> | readonly object[]
}

/** An emoji found by a shortcode or a search, by the shortcode that found it. */
export type FoundEmoji = {
  emoji: string
  label: string
  shortcode: string
  skins?: Readonly<Record<string, string>>
}

export type EmojiCatalog = {
  /** The emoji a shortcode (without its colons, any case) stands for, or none. */
  emojiOf(shortcode: string): FoundEmoji | undefined
  /**
   * The emoji a partly typed shortcode finds, likeliest first, `limit` at most: a shortcode it is,
   * then a shortcode or name with it as a whole word (`party` finds Party popper before Partying
   * face), then a shortcode it starts, then one with a word it starts, then a name, then a tag.
   */
  search(query: string, limit?: number): FoundEmoji[]
  /**
   * An emoji's name, as a screen reader says it ("thumbs up"), whatever skin tone it comes in, or
   * none for one the catalog doesn't hold.
   */
  nameOf(emoji: string): string | undefined
}

/** A skin tone's modifier, as a hexcode writes it. */
const toneOf = /^1F3F[B-F]$/

/** Emoji code points as Emojibase writes a hexcode, without presentation selectors. */
const hexOf = (emoji: string) =>
  [...emoji]
    .map((point) => point.codePointAt(0)!)
    .filter((point) => point !== 0xfe0f && point !== 0xfe0e)
    .map((point) => point.toString(16).toUpperCase())
    .join("-")

const withoutSelectors = (hexcode: string) =>
  hexcode
    .split("-")
    .filter((part) => part !== "FE0F" && part !== "FE0E")
    .join("-")

const wordsOf = (text: string) => text.toLowerCase().split(/[^\p{L}\p{N}+]+/u)

/**
 * The catalog of `emojis` (in the order they are offered) by `shortcodes`, Emojibase's map of
 * hexcode to a shortcode or several. A skin tone's own shortcodes (`+1::skin-tone-2`) are left out:
 * the picker offers the tones.
 */
export function catalogOf(
  emojis: readonly EmojiEntry[],
  shortcodes: Readonly<Record<string, string | readonly string[]>>,
): EmojiCatalog {
  const codesOf = new Map<string, string[]>()
  for (const [hexcode, codes] of Object.entries(shortcodes)) {
    const names = (typeof codes === "string" ? [codes] : codes).filter((n) => !n.includes(":"))
    if (names.length > 0) codesOf.set(withoutSelectors(hexcode), names)
  }
  const entries = emojis.flatMap(({ emoji, label, tags = [], skins: tones }) => {
    const codes = codesOf.get(hexOf(emoji))
    const skins =
      tones && !Array.isArray(tones) ? (tones as Readonly<Record<string, string>>) : undefined
    return codes
      ? [
          {
            emoji,
            label,
            skins,
            codes,
            labelWords: wordsOf(label),
            tags: tags.map((t) => t.toLowerCase()),
          },
        ]
      : []
  })
  const byCode = new Map<string, FoundEmoji>()
  for (const { emoji, label, codes } of entries)
    for (const code of codes)
      if (!byCode.has(code)) byCode.set(code, { emoji, label, shortcode: code })

  const names = new Map(emojis.map(({ emoji, label }) => [hexOf(emoji), label]))

  return {
    emojiOf: (shortcode) => byCode.get(shortcode.toLowerCase()),
    nameOf(emoji) {
      const hex = hexOf(emoji)
      return (
        names.get(hex) ??
        names.get(
          hex
            .split("-")
            .filter((part) => !toneOf.test(part))
            .join("-"),
        )
      )
    },
    search(query, limit = 8) {
      const q = query.toLowerCase()
      if (q === "") return []
      const found: { rank: number; at: number; emoji: FoundEmoji }[] = []
      entries.forEach(({ emoji, label, skins, codes, labelWords, tags }, at) => {
        const ranked = (rank: number, shortcode = codes[0]!) =>
          found.push({ rank, at, emoji: { emoji, label, shortcode, ...(skins && { skins }) } })
        const exact = codes.find((code) => code === q)
        if (exact) return ranked(0, exact)
        const partsOf = (code: string) => code.split(/[_-]/)
        const whole = codes.find((code) => partsOf(code).includes(q))
        if (whole) return ranked(1, whole)
        if (labelWords.includes(q)) return ranked(1)
        const starts = codes.find((code) => code.startsWith(q))
        if (starts) return ranked(2, starts)
        const word = codes.find((code) => partsOf(code).some((part) => part.startsWith(q)))
        if (word) return ranked(3, word)
        if (labelWords.some((part) => part.startsWith(q))) return ranked(4)
        if (tags.some((tag) => tag.startsWith(q))) return ranked(5)
      })
      return found
        .sort((a, b) => a.rank - b.rank || a.at - b.at)
        .slice(0, limit)
        .map(({ emoji }) => emoji)
    },
  }
}
